import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BookOpen, TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Checkbook() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [filterType, setFilterType] = useState('all'); // 'all', 'income', 'expense'

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        const [incomeRes, expensesRes] = await Promise.all([
          axios.get(`${API}/income`, { 
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } 
          }),
          axios.get(`${API}/expenses`, { 
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } 
          })
        ]);

        if (!isMounted) return;

        // Combine and format transactions
        const income = incomeRes.data.map(item => ({
          ...item,
          type: 'income',
          description: item.category || item.source || 'Income',
          vendor: item.source || item.customer || '-'
        }));

        const expenses = expensesRes.data.map(item => ({
          ...item,
          type: 'expense',
          description: item.category,
          vendor: item.vendor
        }));

        const combined = [...income, ...expenses];
        setTransactions(combined);
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch transactions:', error);
        if (isMounted) setLoading(false);
      }
    };
    
    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortedTransactions = () => {
    let filtered = transactions;
    
    // Apply filter
    if (filterType !== 'all') {
      filtered = transactions.filter(t => t.type === filterType);
    }

    // Apply sort
    return [...filtered].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Special handling for date sorting
      if (sortConfig.key === 'date') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      // Special handling for amount - sort by absolute value
      if (sortConfig.key === 'amount') {
        aVal = Number(aVal);
        bVal = Number(bVal);
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const calculateRunningBalance = (sortedTransactions) => {
    let balance = 0;
    return sortedTransactions.map(t => {
      if (t.type === 'income') {
        balance += t.amount;
      } else {
        balance -= t.amount;
      }
      return { ...t, balance };
    });
  };

  const sortedTransactions = getSortedTransactions();
  const transactionsWithBalance = calculateRunningBalance(sortedTransactions);

  const totalIncome = transactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + t.amount, 0);
  
  const totalExpenses = transactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + t.amount, 0);

  const netBalance = totalIncome - totalExpenses;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-2 md:gap-3">
          <div className="p-1.5 md:p-2 bg-blue-100 rounded-lg">
            <BookOpen className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold">Checkbook</h1>
            <p className="text-xs md:text-sm text-slate-500">Complete transaction history</p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-2 md:gap-4 mb-4 md:mb-6">
        <Card className="bg-emerald-950/40 border-emerald-800">
          <CardContent className="p-2.5 md:pt-6 md:p-6">
            <p className="text-[10px] md:text-sm font-medium text-emerald-400">Income</p>
            <p className="text-sm md:text-2xl font-bold text-emerald-300 truncate">
              ${totalIncome.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-red-950/40 border-red-800">
          <CardContent className="p-2.5 md:pt-6 md:p-6">
            <p className="text-[10px] md:text-sm font-medium text-red-400">Expenses</p>
            <p className="text-sm md:text-2xl font-bold text-red-300 truncate">
              ${totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>

        <Card className={`${netBalance >= 0 ? 'bg-blue-950/40 border-blue-800' : 'bg-orange-950/40 border-orange-800'}`}>
          <CardContent className="p-2.5 md:pt-6 md:p-6">
            <p className={`text-[10px] md:text-sm font-medium ${netBalance >= 0 ? 'text-cyan-400' : 'text-orange-400'}`}>Net</p>
            <p className={`text-sm md:text-2xl font-bold truncate ${netBalance >= 0 ? 'text-cyan-300' : 'text-orange-300'}`}>
              ${Math.abs(netBalance).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filter Buttons */}
      <div className="flex gap-2 mb-4 overflow-x-auto">
        <Button
          variant={filterType === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterType('all')}
          data-testid="checkbook-filter-all"
          className="shrink-0 text-xs md:text-sm"
        >
          All ({transactions.length})
        </Button>
        <Button
          variant={filterType === 'income' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterType('income')}
          className={`shrink-0 text-xs md:text-sm ${filterType === 'income' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
          data-testid="checkbook-filter-income"
        >
          Income ({transactions.filter(t => t.type === 'income').length})
        </Button>
        <Button
          variant={filterType === 'expense' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterType('expense')}
          className={`shrink-0 text-xs md:text-sm ${filterType === 'expense' ? 'bg-red-600 hover:bg-red-700' : ''}`}
          data-testid="checkbook-filter-expense"
        >
          Expenses ({transactions.filter(t => t.type === 'expense').length})
        </Button>
      </div>

      {/* Transactions Table */}
      <Card className="shadow-sm">
        <CardHeader className="p-3 md:p-6">
          <CardTitle className="text-base md:text-lg">Transaction Register</CardTitle>
          <CardDescription className="text-xs md:text-sm">
            {transactionsWithBalance.length} transaction{transactionsWithBalance.length !== 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-500">Loading transactions...</p>
            </div>
          ) : transactionsWithBalance.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500">No transactions yet</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-3 p-3" data-testid="checkbook-mobile-list">
                {transactionsWithBalance.map((transaction) => (
                  <Card
                    key={`${transaction.type}-${transaction.id}`}
                    className={`border-l-4 ${
                      transaction.type === 'income' ? 'border-l-emerald-500' : 'border-l-red-500'
                    }`}
                    data-testid={`checkbook-card-${transaction.type}-${transaction.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-slate-900">{transaction.vendor}</div>
                          <div className="text-xs text-slate-500">{transaction.date}</div>
                        </div>
                        <div className={`text-lg font-bold ${
                          transaction.type === 'income' ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          {transaction.type === 'income' ? '+' : '-'}
                          ${transaction.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          {transaction.type === 'income' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                              <TrendingUp className="w-3 h-3" />
                              Income
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <TrendingDown className="w-3 h-3" />
                              Expense
                            </span>
                          )}
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {transaction.description}
                          </span>
                        </div>
                        {transaction.notes && (
                          <div className="text-xs text-slate-600">
                            <span className="font-medium">Notes:</span> {transaction.notes}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-between mt-3 pt-3 border-t">
                        <span className="text-xs text-slate-500">Running Balance</span>
                        <span className={`text-sm font-bold ${
                          transaction.balance >= 0 ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          ${transaction.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => handleSort('date')}
                      >
                        Date
                      </TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => handleSort('vendor')}
                      >
                        Vendor/Source
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => handleSort('description')}
                      >
                        Category
                      </TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead 
                        className="text-right cursor-pointer hover:bg-slate-50"
                        onClick={() => handleSort('amount')}
                      >
                        Amount
                      </TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactionsWithBalance.map((transaction) => (
                      <TableRow 
                        key={`${transaction.type}-${transaction.id}`}
                        className={transaction.type === 'income' ? 'bg-emerald-50/30' : 'bg-red-50/30'}
                      >
                        <TableCell className="font-medium">{transaction.date}</TableCell>
                        <TableCell>
                          {transaction.type === 'income' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                              <TrendingUp className="w-3 h-3" />
                              Income
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <TrendingDown className="w-3 h-3" />
                              Expense
                            </span>
                          )}
                        </TableCell>
                        <TableCell>{transaction.vendor}</TableCell>
                        <TableCell>
                          <span className={`inline-flex px-2 py-1 rounded text-xs ${
                            transaction.category === 'Tax Deposits' 
                              ? 'bg-green-100 text-green-800 font-medium' 
                              : 'bg-slate-100 text-slate-700'
                          }`}>
                            {transaction.description}
                          </span>
                        </TableCell>
                        <TableCell className="max-w-xs truncate text-sm text-slate-600">
                          {transaction.notes || '-'}
                        </TableCell>
                        <TableCell className={`text-right font-semibold ${
                          transaction.type === 'income' ? 'text-emerald-700' : 'text-red-700'
                        }`}>
                          {transaction.type === 'income' ? '+' : '-'}
                          ${transaction.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className={`text-right font-bold ${
                          transaction.balance >= 0 ? 'text-blue-700' : 'text-orange-700'
                        }`}>
                          ${transaction.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
